import type { MouseEvent } from 'react';
import { useState } from 'react';

import { DeleteOutlined, PaperClipOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Image, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useCreateReviewMutation } from '@/api/endpoints/mainPages';
import { DataSection, ImageUploadField, InputField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import { REVIEW_VALIDATION_SCHEMA } from '../../consts';
import type { IFormData } from '../../types';

import styles from './ReviewModals.module.scss';

type TProps = {
  pageId: number;
  isModalOpen: boolean;
  handleCancel: VoidFunction;
};

export const CreateReviewModal = ({ pageId, isModalOpen, handleCancel }: TProps) => {
  const [createReview, { isLoading: isReviewCreating }] = useCreateReviewMutation();

  const sendApiRequest = useApiRequest();

  const methods = useForm<IFormData>({
    defaultValues: {
      name: '',
      description: '',
      photo: null,
    },
    resolver: yupResolver(REVIEW_VALIDATION_SCHEMA),
  });

  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const { handleSubmit, watch, resetField } = methods;

  const handleCreateReview = handleSubmit(({ name, description, photo }) => {
    if (photo) {
      sendApiRequest({
        successMessage: 'Отзыв успешно создан',
        apiRequestCallback: () =>
          createReview({
            id: pageId,
            data: {
              name,
              description,
              photo: photo.signed_id ?? '',
            },
          }),
        successCallback: handleCancel,
      });
    }
  });

  const photo = watch('photo');

  const getImageName = () => {
    if (!photo) {
      return '';
    }

    return photo.filename;
  };

  const removeUploadedImage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    resetField('photo');
    setImageUrl('');
  };

  const imageRequestCallback = (file: File) => {
    const url = URL.createObjectURL(file);
    setImageUrl(url);
  };

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={isReviewCreating}>
        <div className={styles.wrapper}>
          <FormProvider {...methods}>
            <p className={styles.title}>Добавление отзыва</p>
            <div className={styles['content-wrapper']}>
              <DataSection
                titleText="Имя и фамилия студента"
                isTitleRequired
              >
                <InputField
                  fieldName="name"
                  placeholder="Имя и фамилия"
                  shouldEnableLengthCounter
                  maxLengthCount={200}
                />
              </DataSection>
              <DataSection
                titleText="Текст отзыва"
                isTitleRequired
              >
                <InputField
                  fieldName="description"
                  placeholder="Текст"
                  shouldEnableLengthCounter
                  maxLengthCount={500}
                />
              </DataSection>
              <DataSection
                titleText="Фото студента"
                isTitleRequired
              >
                <ImageUploadField
                  accept="image/png,image/jpeg,image/jpg"
                  name="Фото студента"
                  fieldName="photo"
                  className={photo ? styles['image-uploaded'] : ''}
                  requestCallback={imageRequestCallback}
                  showUploadList={false}
                />
                {photo && (
                  <>
                    <Button
                      size="small"
                      onClick={() => setIsImagePreviewOpen(true)}
                      type="text"
                      icon={<PaperClipOutlined />}
                      className={styles['image-button-wrapper']}
                    >
                      {getImageName()}
                      <DeleteOutlined
                        className={styles['remove-image-icon']}
                        onClick={removeUploadedImage}
                      />
                    </Button>
                    <Image
                      alt="Фото студента"
                      style={{ display: 'none' }}
                      preview={{
                        visible: isImagePreviewOpen,
                        src: imageUrl,
                        onVisibleChange: (value) => setIsImagePreviewOpen(value),
                      }}
                    />
                  </>
                )}
              </DataSection>
            </div>
            <div className={styles['buttons-wrapper']}>
              <Button
                size="large"
                onClick={handleCancel}
                className={styles.button}
                danger
              >
                Отмена
              </Button>
              <Button
                size="large"
                onClick={handleCreateReview}
                disabled={isReviewCreating}
                type="primary"
                className={styles.button}
              >
                Сохранить
              </Button>
            </div>
          </FormProvider>
        </div>
      </Spin>
    </Modal>
  );
};
